import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn, prettyJson } from '@/lib/utils';
import { PayloadSection } from '@/modules/sentinel-workspace/tool-call-card';
import type { ToolRunResult as ToolRunResultData } from '@/services';
import { toolRunOutcome, type ToolRunTone } from './tool-run-outcome';

const BADGE: Record<ToolRunTone, { variant: 'success' | 'warning' | 'secondary' | 'destructive'; label: string }> = {
	success: { variant: 'success', label: 'Success' },
	partial: { variant: 'warning', label: 'Partial' },
	neutral: { variant: 'secondary', label: 'Nothing found' },
	error: { variant: 'destructive', label: 'Error' }
};

/** One run's answer: what it means, how long it took, and what the tool returned. */
export const ToolRunResult = ({ result, writes }: { result: ToolRunResultData; writes: boolean }) => {
	const [showRaw, setShowRaw] = useState(false);
	const outcome = toolRunOutcome(result, writes);
	const badge = BADGE[outcome.tone];

	return (
		<section aria-label='Run result' className='flex flex-col gap-3 rounded-xl border border-gray-500 p-4'>
			<div className='flex flex-wrap items-center gap-2'>
				<Badge variant={badge.variant}>{badge.label}</Badge>
				<p className='text-[13px] font-medium leading-4.5 text-text-heading'>{outcome.title}</p>
				<span className='ml-auto text-xs text-gray-800'>{`${result.latencyMs} ms`}</span>
			</div>
			{outcome.hint && <p className='text-xs leading-4 text-text-heading'>{outcome.hint}</p>}
			{result.status === 'error' ? (
				<p className={cn('text-xs leading-4 wrap-break-word', outcome.tone === 'error' ? 'text-red-600' : 'text-gray-800')}>
					<span className='font-mono'>{result.code}</span>
					{result.message && ` · ${result.message}`}
				</p>
			) : showRaw ? (
				<pre className='max-h-96 overflow-auto rounded-[10px] bg-gray-100 p-2 font-mono text-[11px] leading-4 text-text-body'>
					{prettyJson(result.data)}
				</pre>
			) : (
				<PayloadSection title='OUTPUT' payload={result.data} />
			)}
			<div className='flex items-center justify-between gap-2 text-xs text-gray-800'>
				<span className='font-mono'>{result.requestId}</span>
				{result.status === 'success' && (
					<button type='button' className='text-indigo-500 hover:underline' onClick={() => setShowRaw((raw) => !raw)}>
						{showRaw ? 'Show fields' : 'Show raw JSON'}
					</button>
				)}
			</div>
		</section>
	);
};
